import { Pause, Play } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { OptionCard } from "@/components/agents/setup/receptionist/option-card"
import type { VoiceOption } from "@/components/agents/setup/types"
import { cn } from "@/lib/utils"

interface VoicePickerProps {
  voices: VoiceOption[]
  selected: string
  onSelect: (id: string) => void
  columns?: 2 | 3
}

export function VoicePicker({
  voices,
  selected,
  onSelect,
  columns = 2,
}: VoicePickerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)

  // Stop playback on unmount so audio doesn't keep going after the sheet
  // closes or the step unmounts.
  useEffect(() => {
    return () => {
      audioRef.current?.pause()
      audioRef.current = null
    }
  }, [])

  function togglePreview(voice: VoiceOption) {
    if (!voice.preview_audio_url) return

    if (playingId === voice.id) {
      audioRef.current?.pause()
      setPlayingId(null)
      return
    }

    audioRef.current?.pause()
    const audio = new Audio(voice.preview_audio_url)
    audio.onended = () => setPlayingId(null)
    audio.onerror = () => setPlayingId(null)
    audioRef.current = audio
    void audio.play()
    setPlayingId(voice.id)
  }

  return (
    <div
      className={cn(
        "grid gap-3",
        columns === 3 ? "grid-cols-3" : "grid-cols-2",
      )}
    >
      {voices.map((v) => {
        const active = selected === v.id
        const playing = playingId === v.id
        return (
          <OptionCard
            key={v.id}
            active={active}
            onClick={() => onSelect(v.id)}
            className="flex items-center gap-3"
          >
            <span
              role="button"
              tabIndex={0}
              aria-label={
                playing ? `Pause ${v.name} preview` : `Play ${v.name} preview`
              }
              onClick={(e) => {
                e.stopPropagation()
                togglePreview(v)
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  e.stopPropagation()
                  togglePreview(v)
                }
              }}
              className={cn(
                "bg-background hover:bg-muted flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors",
                !v.preview_audio_url && "cursor-not-allowed opacity-50",
                active && "border-blue-300 dark:border-blue-600",
              )}
            >
              {playing ? (
                <Pause
                  className={cn(
                    "size-3.5",
                    active
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-foreground",
                  )}
                  fill="currentColor"
                />
              ) : (
                <Play
                  className={cn(
                    "ml-0.5 size-3.5",
                    active
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-foreground",
                  )}
                  fill="currentColor"
                />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "text-sm leading-tight font-semibold",
                  active && "text-blue-600 dark:text-blue-400",
                )}
              >
                {v.name}
              </p>
              <p
                className={cn(
                  "mt-0.5 text-xs leading-snug",
                  active
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-muted-foreground",
                )}
              >
                {v.description}
              </p>
            </div>
          </OptionCard>
        )
      })}
    </div>
  )
}
